import { useParams, useNavigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { getGameById } from '@/data/games'
import { applyGameManifest } from '@/hooks/usePWA'

// Lazy load game components
const TicTacToe = lazy(() => import('@/games/classic/TicTacToe').then(m => ({ default: m.TicTacToe })))
const Snake = lazy(() => import('@/games/arcade/Snake').then(m => ({ default: m.Snake })))
const Breakout = lazy(() => import('@/games/arcade/Breakout').then(m => ({ default: m.Breakout })))
const Pong = lazy(() => import('@/games/arcade/Pong').then(m => ({ default: m.Pong })))
const MemoryMatch = lazy(() => import('@/games/puzzle/MemoryMatch').then(m => ({ default: m.MemoryMatch })))
const CarRacing = lazy(() => import('@/games/racing/CarRacing').then(m => ({ default: m.CarRacing })))
const CarDodger = lazy(() => import('@/games/racing/CarDodger').then(m => ({ default: m.CarDodger })))
const NeonDrift = lazy(() => import('@/games/racing/neon-drift').then(m => ({ default: m.NeonDrift })))
const SkyStack = lazy(() => import('@/games/casual/sky-stack').then(m => ({ default: m.SkyStack })))
const JumpBox = lazy(() => import('@/games/casual/jump-box').then(m => ({ default: m.JumpBox })))

const gameComponentsMap = {
  'TicTacToe': TicTacToe,
  'Snake': Snake,
  'Breakout': Breakout,
  'Pong': Pong,
  'MemoryMatch': MemoryMatch,
  'CarRacing': CarRacing,
  'CarDodger': CarDodger,
  'NeonDrift': NeonDrift,
  'SkyStack': SkyStack,
  'JumpBox': JumpBox,
}

// Loading component
function GameLoading() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]">
      <div className="text-center">
        <div className="animate-[fadeInOut_2s_ease-in-out_infinite]">
          <svg viewBox="0 0 50 50" className="w-16 h-16 mx-auto">
            <path 
              d="M25 0.055c-4.227 0 -8.21 1.057 -11.696 2.91l21.79 7.019c8.573 2.752 13.446 11.909 10.741 20.497 -2.263 7.169 -9.03 11.672 -16.191 11.404 -2.295 -0.087 -3.864 -2.374 -3.155 -4.566 0.481 -1.498 1.901 -2.476 3.47 -2.421 4.101 0.134 7.973 -2.476 9.227 -6.609 1.498 -4.897 -1.325 -10.071 -6.199 -11.64L6.68 8.084C2.571 12.524 0.055 18.47 0.055 25c0 7.831 3.612 14.819 9.259 19.393L16.325 22.587c0.591 -1.838 2.563 -2.847 4.401 -2.256s2.847 2.563 2.256 4.401l-7.5 23.328c2.934 1.207 6.144 1.885 9.519 1.885 13.778 0 24.945 -11.167 24.945 -24.945S38.778 0.055 25 0.055" 
              fill="#e3ff04"
            />
          </svg>
        </div>
        <p className="text-white/60 mt-4 text-sm">Loading game...</p>
      </div>
      <style>{`
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  )
}

// Standalone Game Player Page - Clean fullscreen game only
export function StandaloneGamePage() {
  const { gameId } = useParams()
  const navigate = useNavigate()
  
  const game = getGameById(gameId)
  
  useEffect(() => {
    // Set document title and apply game-specific manifest
    if (game) {
      document.title = `${game.title} | Polytoria`
      const cleanup = applyGameManifest(game)
      
      // Hide scrollbars for standalone mode
      document.body.style.overflow = 'hidden'
      
      return () => {
        cleanup?.()
        document.body.style.overflow = ''
      }
    }
  }, [game])
  
  if (!game || !game.playable || !game.component) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Game Not Found</h1>
          <button 
            onClick={() => navigate('/')}
            className="bg-game-primary text-black font-bold px-6 py-2 rounded-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }
  
  const GameComponent = gameComponentsMap[game.component]
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Game Content - Full screen, no bars */}
      <div className="flex-1 relative overflow-hidden">
        <Suspense fallback={<GameLoading />}>
          <GameComponent gameInfo={game} standalone={true} />
        </Suspense>
      </div>
    </div>
  )
}
